import {
  DialogContent,
  ListSubheader,
  MenuItem,
  Button,
  DialogActions,
  useTheme,
  Box,
  InputAdornment,
} from '@material-ui/core'
import React, { memo, useCallback, useEffect, useState } from 'react'
import { useForm, useWatch, ValidateResult } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useReactiveVar } from '@apollo/client'
import { CostType, ProjectFragment } from '../../generated/graphql'
import { Dialog } from '../dialog/Dialog'
import { ControlledTextField } from '../form/ControlledTextField'
import { FormGroup } from '../form/FormGroup'
import { validators } from '../../validation/validators'
import { EMPTY_VALUE } from '../common/constants'
import { pricePerYearUnit, priceUnit } from '../../util/formatters'
import { ControlledOccurenceYear } from './ControlledOccurenceYear'
import { dictionariesVar } from '../../apollo/reactive-vars'
import { SystemContainer } from '../common/util/dictionary-util'

export type CostsDialogFormValues = {
  projectVersionId: string
  providerId: string
  costType: CostType
  recurrencePeriodInYears: string
  occurrenceYear: string
  endYear: string
  value: string
  description: string
}

type Props = {
  project: ProjectFragment
  isOpen: boolean
  onClose: () => void
  onSubmit: (values: CostsDialogFormValues) => Promise<void>
  onDelete: () => Promise<void>
  defaultValues: Partial<CostsDialogFormValues>
  costId?: string
}

function CostsDialog({
  project,
  isOpen,
  onClose,
  onSubmit,
  onDelete,
  defaultValues,
  costId,
}: Props): React.ReactElement {
  const theme = useTheme()
  const { t } = useTranslation()
  const { getSystemContainerTitle } = useReactiveVar(dictionariesVar)

  const [systemContainers, setSystemContainers] = useState<SystemContainer[]>(
    [],
  )
  const getBuildingYear = useCallback(
    (versionId?: string) =>
      project.versions.find((version) => version.id === versionId)?.pricingModel
        ?.buildingYear,
    [project],
  )
  const [buildingYear, setBuildingYear] = useState(
    getBuildingYear(defaultValues.projectVersionId),
  )

  const [costType, setCostType] = useState<CostType>(CostType.Initial)

  const recurrencePeriodInYearsEnabled =
    costType === CostType.Renewal || costType === CostType.OtherRecurring
  const occurrenceYearEnabled =
    costType === CostType.Initial || costType === CostType.OtherOneTime
  const endYearEnabled =
    costType === CostType.Upkeep || costType === CostType.OtherAnnual

  const getSystemContainers = useCallback(
    (projectVersionId: string) => {
      const projectVersion = project.versions.find(
        (version) => version.id === projectVersionId,
      )
      if (!projectVersion) {
        throw new Error('Project version missing')
      }
      return [...projectVersion.buildings, ...projectVersion.localGrids]
    },
    [project],
  )

  const defaultVersion = project.versions.find((version) => version.isDefault)

  useEffect(() => {
    const defaultSystemContainers = getSystemContainers(
      defaultVersion?.id || '',
    )
    setSystemContainers(defaultSystemContainers)
  }, [defaultVersion, getSystemContainers])

  const { control, errors, handleSubmit, setValue, getValues, trigger, reset } =
    useForm<CostsDialogFormValues>({ defaultValues })

  const formValues = useWatch<CostsDialogFormValues>({ control })

  useEffect(() => {
    if (control.fieldsRef.current.description) {
      const triggerDescription = async () => trigger('description')
      void triggerDescription()
    }
    // Validation should only be triggered when formvalues change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues])

  useEffect(() => {
    const triggerRecurrencePeriodInYears = async () => {
      if (recurrencePeriodInYearsEnabled) {
        await trigger('recurrencePeriodInYears')
      }
    }
    void triggerRecurrencePeriodInYears()
  }, [recurrencePeriodInYearsEnabled, trigger])

  const checkIfDescriptionExists = (
    providerId: string,
    description: string,
  ) => {
    const versionId = getValues('projectVersionId')
    const provider = providerId === EMPTY_VALUE ? null : providerId

    const { costs } = project.versions.find(
      (version) => version.id === versionId,
    ) ?? { costs: [] }

    return costs
      .filter(
        (cost) => cost.providerId === provider && cost.costType === costType,
      )
      .filter((cost) => cost.id !== costId)
      .map((cost) => cost.description)
      .includes(description)
  }

  const checkIfProviderHasCost = (providerId: string) => {
    const versionId = getValues('projectVersionId')
    if (costId) {
      return false
    }
    return project.versions
      .filter((version) => version.id === versionId)
      .flatMap((version) => version.costs)
      .some(
        (cost) => cost.providerId === providerId && cost.costType === costType,
      )
  }

  const validateDescription = (): ValidateResult => {
    const providerId = getValues('providerId')
    const description = getValues('description')
    const providerIsEmpty = providerId === EMPTY_VALUE
    const providerHasCost = checkIfProviderHasCost(providerId)
    const descriptionExists =
      description.length > 0
        ? checkIfDescriptionExists(providerId, description)
        : false

    if (providerHasCost && !description) {
      return String(t('validation.requiredProviderDuplicate'))
    }
    if (providerIsEmpty && !description) {
      return String(t('validation.requiredNoProvider'))
    }
    if (descriptionExists) {
      return String(t('validation.requiredDescriptionDuplicate'))
    }
    return true
  }

  useEffect(() => {
    reset(defaultValues)
    const { projectVersionId, costType: type } = defaultValues
    if (projectVersionId) {
      const containers = getSystemContainers(projectVersionId)
      setSystemContainers(containers)
      setBuildingYear(getBuildingYear(projectVersionId))
    }
    if (type) {
      setCostType(type)
    }
  }, [defaultValues, reset, getSystemContainers, getBuildingYear])

  useEffect(() => {
    const projectVersionId =
      getValues('projectVersionId') ?? defaultValues.projectVersionId
    const buildingYearInPricingModel = getBuildingYear(projectVersionId)
    setBuildingYear(buildingYearInPricingModel)
    // Building year should only be updated if the project is updated, e.g. if a pricing model changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project])

  const onProjectVersionIdChange = (projectVersionId: string) => {
    const projectSystemContainers = getSystemContainers(projectVersionId)
    setSystemContainers(projectSystemContainers)
    setBuildingYear(getBuildingYear(projectVersionId))
    setValue('providerId', EMPTY_VALUE)
  }

  const mapContainerToProviderMenuItems = (container: SystemContainer) => [
    <ListSubheader key={container.id}>
      {getSystemContainerTitle(container)}
    </ListSubheader>,
    ...container.providers.map((provider) => (
      <MenuItem key={provider.id} value={provider.id}>
        {provider.name}
      </MenuItem>
    )),
  ]

  const title = costId ? t('costs.dialog.edit') : t('costs.dialog.title')

  const onCostTypeChange = (value: string) => {
    setCostType(value as CostType)
  }

  const handleClose = () => {
    reset({})
    onClose()
  }

  const handleSubmitAndReset = async (values: CostsDialogFormValues) => {
    await onSubmit(values)
    reset({})
  }

  return (
    <Dialog title={title} open={isOpen} onClose={handleClose}>
      <form onSubmit={handleSubmit(handleSubmitAndReset)}>
        <DialogContent dividers>
          <FormGroup>
            <ControlledTextField
              control={control}
              name="costType"
              required
              error={errors.costType}
              label={t('costs.common.costType')}
              select
              disabled={Boolean(costId)}
              onChange={(event) => onCostTypeChange(event.target.value)}
            >
              {Object.keys(CostType).map((type) => (
                <MenuItem key={type} value={type}>
                  {t(`costType.${type}`)}
                </MenuItem>
              ))}
            </ControlledTextField>
          </FormGroup>

          <FormGroup>
            <ControlledTextField
              control={control}
              name="projectVersionId"
              required
              error={errors.projectVersionId}
              label={t('costs.common.projectVersion')}
              disabled={Boolean(costId)}
              onChange={(event) => onProjectVersionIdChange(event.target.value)}
              select
            >
              {project.versions.map((version) => (
                <MenuItem key={version.id} value={version.id}>
                  {version.name}
                </MenuItem>
              ))}
            </ControlledTextField>
          </FormGroup>

          <FormGroup>
            <ControlledTextField
              control={control}
              name="providerId"
              error={errors.providerId}
              disabled={Boolean(costId)}
              label={t('costs.dialog.provider')}
              select
            >
              <MenuItem value={EMPTY_VALUE}>
                {t('costs.common.noProviderSelected')}
              </MenuItem>
              {systemContainers.map(mapContainerToProviderMenuItems)}
            </ControlledTextField>
          </FormGroup>

          <FormGroup>
            <ControlledTextField
              control={control}
              name="description"
              rules={{ validate: () => validateDescription() }}
              error={errors.description}
              type="text"
              label={t('costs.common.description')}
            />
          </FormGroup>

          {recurrencePeriodInYearsEnabled && (
            <FormGroup>
              <ControlledTextField
                control={control}
                name="recurrencePeriodInYears"
                error={errors.recurrencePeriodInYears}
                type="text"
                label={t('costs.common.recurrencePeriodInYears')}
                rules={{
                  ...validators.positiveInteger,
                  ...validators.required,
                }}
              />
            </FormGroup>
          )}

          {occurrenceYearEnabled && (
            <FormGroup>
              <ControlledOccurenceYear
                control={control}
                occurrenceYearError={errors.occurrenceYear}
                defaultValue={defaultValues.occurrenceYear}
                buildingYear={buildingYear}
              />
            </FormGroup>
          )}

          {endYearEnabled && (
            <FormGroup>
              <ControlledTextField
                control={control}
                name="endYear"
                error={errors.endYear}
                type="text"
                label={t('costs.common.endYear')}
                rules={{ ...validators.positiveInteger }}
              />
            </FormGroup>
          )}

          <FormGroup>
            <ControlledTextField
              control={control}
              name="value"
              type="text"
              label={t('costs.common.value')}
              error={errors.value}
              rules={{
                ...validators.decimal,
                ...validators.required,
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {endYearEnabled ? pricePerYearUnit : priceUnit}
                  </InputAdornment>
                ),
              }}
            />
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Box display="flex" flexGrow={1} gridGap={theme.spacing(1)}>
            <Box>
              {costId && (
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={onDelete}
                >
                  {t('common.remove')}
                </Button>
              )}
            </Box>

            <Box flexGrow={1} />
            <Button variant="contained" onClick={handleClose}>
              {t('common.cancel')}
            </Button>

            <Button type="submit" variant="contained" color="primary">
              {costId ? t('common.saveChanges') : t('costs.dialog.save')}
            </Button>
          </Box>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default memo(CostsDialog)
